/**
 * The reconciliation workspace (M06-02–M06-09): statement lines on the left,
 * book entries on the right. Accept a suggestion, pick several to match
 * together, or book what the bank did that the books haven't seen yet.
 */
import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, EyeOff, FileUp, Link2, Plus, Sparkles, Wand2 } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { getAccessToken } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { Decimal, amount, dec, parseAmount } from '../../lib/money';
import { shortDate } from '../../lib/format';
import { manilaToday, useAccounts } from '../../lib/queries';
import { AccountSelect } from '../../components/AccountSelect';
import { Alert, Button, Card, EmptyState, Field, Modal, Money, PageHeader, Skeleton, StatusChip, filterClass, inputClass } from '../../components/ui';
import { useCashAccounts } from './BankAccountsPage';

interface StatementLine { id: string; date: string; description: string; reference: string; amount: string; balance: string | null; status: string; matchId: string | null; reconciled: boolean }
interface BookLine { id: string; entryId: string; number: string | null; date: string; source: string; sourceType: string | null; description: string; reference: string; amount: string }
interface Suggestion { line: StatementLine; candidates: (BookLine & { score: number })[]; rule: { id: string; name: string } | null }
interface Recon { id: string; status: string; statementBalance: string; bookBalance: string; depositsInTransit: string; outstandingChecks: string; unrecordedItems: string; adjustedBank: string; adjustedBook: string; difference: string; canComplete: boolean }

export function ReconcilePage(): JSX.Element {
  const { accountId = '' } = useParams();
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const cash = useCashAccounts();
  const account = cash.data?.find((a) => a.accountId === accountId);
  const mappings = useQuery({ queryKey: ['bank-mappings'], queryFn: () => api<{ presets: { key: string; label: string }[]; saved: { key: string; label: string }[] }>('/bank-statement-mappings') });
  const suggestions = useQuery({ queryKey: ['bank-suggestions', accountId], queryFn: () => api<Suggestion[]>(`/bank-accounts/${accountId}/suggestions`) });
  const book = useQuery({ queryKey: ['bank-book', accountId], queryFn: () => api<BookLine[]>(`/bank-accounts/${accountId}/book-lines`) });
  const [pickedStatement, setPickedStatement] = useState<Set<string>>(new Set());
  const [pickedBook, setPickedBook] = useState<Set<string>>(new Set());
  const [mapping, setMapping] = useState('');
  const [message, setMessage] = useState<{ tone: 'ok' | 'critical' | 'warning'; text: string } | null>(null);
  const [creating, setCreating] = useState<StatementLine | null>(null);
  const [statementDate, setStatementDate] = useState(manilaToday());
  const [statementBalance, setStatementBalance] = useState('');
  const [recon, setRecon] = useState<Recon | null>(null);
  const manage = can('bank.manage');

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['bank-suggestions', accountId] });
    void queryClient.invalidateQueries({ queryKey: ['bank-book', accountId] });
    void queryClient.invalidateQueries({ queryKey: ['cash-accounts'] });
    setPickedBook(new Set());
    setPickedStatement(new Set());
  };
  const fail = (e: unknown): void => setMessage({ tone: 'critical', text: e instanceof ApiError ? e.message : (e as Error).message });

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData();
      form.append('file', file);
      const res = await fetch(`/api/v1/bank-accounts/${accountId}/statement-lines${mapping ? `?mapping=${mapping}` : ''}`, { method: 'POST', body: form, credentials: 'include', headers: { authorization: `Bearer ${getAccessToken() ?? ''}` } });
      const body = (await res.json()) as { imported?: number; duplicates?: number; errors?: string[]; message?: string };
      if (!res.ok) throw new Error(body.message ?? 'Import failed.');
      return body;
    },
    onSuccess: (b) => { setMessage({ tone: b.errors?.length ? 'warning' : 'ok', text: `Imported ${b.imported} line(s); ${b.duplicates} already here.${b.errors?.length ? ` ${b.errors.length} row(s) skipped: ${b.errors[0]}` : ''}` }); refresh(); },
    onError: fail,
  });
  const match = useMutation({ mutationFn: (body: { statementLineIds: string[]; journalLineIds: string[] }) => api('/bank-matches', { method: 'POST', body: { cashAccountId: accountId, ...body } }), onSuccess: () => { setMessage(null); refresh(); }, onError: fail });
  const ignore = useMutation({ mutationFn: (id: string) => api(`/statement-lines/${id}/ignore`, { method: 'POST' }), onSuccess: refresh, onError: fail });
  const rules = useMutation({ mutationFn: () => api<{ booked: number }>(`/bank-accounts/${accountId}/apply-rules`, { method: 'POST' }), onSuccess: (r) => { setMessage({ tone: 'ok', text: `Bank rules booked ${r.booked} line(s).` }); refresh(); }, onError: fail });
  const reconcile = useMutation({ mutationFn: () => api<Recon>('/reconciliations', { method: 'POST', body: { cashAccountId: accountId, statementDate, statementBalance: parseAmount(statementBalance) ?? '0' } }), onSuccess: setRecon, onError: fail });
  const complete = useMutation({ mutationFn: () => api(`/reconciliations/${recon!.id}/complete`, { method: 'POST' }), onSuccess: () => { setMessage({ tone: 'ok', text: 'Reconciliation completed and locked.' }); setRecon(null); refresh(); }, onError: fail });

  const sumOf = (ids: Set<string>, rows: { id: string; amount: string }[]) => rows.filter((r) => ids.has(r.id)).reduce((a, r) => a.plus(dec(r.amount)), new Decimal(0));
  const statementRows = useMemo(() => suggestions.data?.map((s) => s.line) ?? [], [suggestions.data]);
  const pickedStatementSum = sumOf(pickedStatement, statementRows);
  const pickedBookSum = sumOf(pickedBook, book.data ?? []);
  const toggle = (set: Set<string>, id: string, fn: (s: Set<string>) => void): void => { const next = new Set(set); if (next.has(id)) next.delete(id); else next.add(id); fn(next); };

  return (
    <>
      <PageHeader
        title={account ? `${account.code} ${account.name}` : 'Reconcile'}
        description={account ? `Book balance ${amount(account.balance)}${account.lastReconciled ? ` · last reconciled ${shortDate(account.lastReconciled)}` : ''}` : undefined}
        actions={<Link to="/banking"><Button variant="secondary">All accounts</Button></Link>}
      />
      {message && <div className="mb-4"><Alert tone={message.tone}>{message.text}</Alert></div>}
      {manage && (
        <Card className="mb-4">
          <div className="flex flex-wrap items-end gap-3">
            <Field label="Statement layout" hint="OFX files need no layout.">
              <select className={`${filterClass} w-64`} value={mapping} onChange={(e) => setMapping(e.target.value)}>
                <option value="">Choose the bank</option>
                {(mappings.data?.presets ?? []).map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
                {(mappings.data?.saved ?? []).map((p) => <option key={p.key} value={p.key}>{p.label} (saved)</option>)}
              </select>
            </Field>
            <label className="inline-flex min-h-10 cursor-pointer items-center gap-2 rounded-xl bg-navy px-4 py-2 text-sm font-semibold text-white hover:bg-navy-700">
              <FileUp aria-hidden="true" className="h-4 w-4" /> {upload.isPending ? 'Importing…' : 'Import statement'}
              <input type="file" accept=".csv,.xlsx,.ofx,.qfx" className="sr-only" onChange={(e) => { const f = e.target.files?.[0]; if (f) upload.mutate(f); e.target.value = ''; }} />
            </label>
            <Button variant="secondary" busy={rules.isPending} onClick={() => rules.mutate()}><Wand2 aria-hidden="true" className="h-4 w-4" /> Apply bank rules</Button>
            <Link to="/banking/rules" className="text-sm text-brand-700 hover:underline dark:text-brand-100">Manage rules</Link>
          </div>
        </Card>
      )}

      {pickedStatement.size > 0 && pickedBook.size > 0 && (
        <div className="sticky top-16 z-10 mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-brand/30 bg-brand-50 px-4 py-3 text-sm dark:bg-slate-800">
          <span>Bank <Money className="font-semibold">{amount(pickedStatementSum.toString())}</Money> ↔ Books <Money className="font-semibold">{amount(pickedBookSum.toString())}</Money>{!pickedStatementSum.eq(pickedBookSum) && <span className="ml-2 text-red-600">differ by {amount(pickedStatementSum.minus(pickedBookSum).abs().toString())}</span>}</span>
          <Button size="sm" disabled={!pickedStatementSum.eq(pickedBookSum)} busy={match.isPending} onClick={() => match.mutate({ statementLineIds: [...pickedStatement], journalLineIds: [...pickedBook] })}><Link2 aria-hidden="true" className="h-4 w-4" /> Match selected</Button>
        </div>
      )}

      <div className="grid gap-4 xl:grid-cols-2">
        <Card title="Bank statement" description="Lines the bank reported that aren't matched yet.">
          {suggestions.isLoading && <Skeleton className="h-40 w-full" />}
          {suggestions.data?.length === 0 && <EmptyState title="All caught up" description="Import the latest statement to continue." />}
          <ul className="divide-y divide-slate-100 dark:divide-slate-800">
            {suggestions.data?.map((s) => (
              <li key={s.line.id} className="py-3">
                <div className="flex items-start gap-3">
                  {manage && <input type="checkbox" className="mt-1 h-4 w-4" checked={pickedStatement.has(s.line.id)} onChange={() => toggle(pickedStatement, s.line.id, setPickedStatement)} aria-label={`Select ${s.line.description}`} />}
                  <div className="min-w-0 flex-1">
                    <div className="flex justify-between gap-3">
                      <span className="truncate font-medium">{s.line.description}</span>
                      <Money className={dec(s.line.amount).isNegative() ? 'text-red-600' : 'text-emerald-700 dark:text-emerald-400'}>{amount(s.line.amount)}</Money>
                    </div>
                    <p className="text-xs text-slate-500">{shortDate(s.line.date)}{s.line.reference ? ` · ${s.line.reference}` : ''}</p>
                    {manage && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {s.candidates.slice(0, 1).map((c) => (
                          <Button key={c.id} size="sm" busy={match.isPending} onClick={() => match.mutate({ statementLineIds: [s.line.id], journalLineIds: [c.id] })}>
                            <Sparkles aria-hidden="true" className="h-3.5 w-3.5" /> Match {c.number} ({shortDate(c.date)})
                          </Button>
                        ))}
                        <Button size="sm" variant="secondary" onClick={() => setCreating(s.line)}><Plus aria-hidden="true" className="h-3.5 w-3.5" /> {s.rule ? `Book as ${s.rule.name}` : 'Book it'}</Button>
                        <Button size="sm" variant="ghost" onClick={() => ignore.mutate(s.line.id)}><EyeOff aria-hidden="true" className="h-3.5 w-3.5" /> Ignore</Button>
                      </div>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </Card>
        <Card title="In the books" description="Receipts, payments and entries on this account not matched yet.">
          {book.isLoading && <Skeleton className="h-40 w-full" />}
          {book.data?.length === 0 && <EmptyState title="Nothing unmatched" />}
          <ul className="divide-y divide-slate-100 dark:divide-slate-800">
            {book.data?.map((b) => (
              <li key={b.id} className="flex items-start gap-3 py-3">
                {manage && <input type="checkbox" className="mt-1 h-4 w-4" checked={pickedBook.has(b.id)} onChange={() => toggle(pickedBook, b.id, setPickedBook)} aria-label={`Select ${b.number}`} />}
                <div className="min-w-0 flex-1">
                  <div className="flex justify-between gap-3">
                    <Link to={`/journals/${b.entryId}`} className="truncate font-medium hover:underline">{b.description || b.number}</Link>
                    <Money className={dec(b.amount).isNegative() ? 'text-red-600' : 'text-emerald-700 dark:text-emerald-400'}>{amount(b.amount)}</Money>
                  </div>
                  <p className="text-xs text-slate-500">{shortDate(b.date)} · {b.number}{b.reference ? ` · ${b.reference}` : ''}</p>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      </div>

      {manage && (
        <Card title="Reconcile to the statement" className="mt-4" description="Enter the closing balance printed on the bank statement.">
          <div className="flex flex-wrap items-end gap-3">
            <Field label="Statement date"><input type="date" className={inputClass} value={statementDate} onChange={(e) => setStatementDate(e.target.value)} /></Field>
            <Field label="Statement closing balance"><input className={`${inputClass} text-right`} inputMode="decimal" value={statementBalance} onChange={(e) => setStatementBalance(e.target.value)} /></Field>
            <Button variant="secondary" busy={reconcile.isPending} disabled={!parseAmount(statementBalance.replace('-', ''))} onClick={() => reconcile.mutate()}>Check</Button>
          </div>
          {recon && (
            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <dl className="space-y-1 text-sm">
                <div className="flex justify-between"><dt className="text-slate-500">Balance per bank</dt><dd><Money>{amount(recon.statementBalance)}</Money></dd></div>
                <div className="flex justify-between"><dt className="text-slate-500">Add: deposits in transit</dt><dd><Money>{amount(recon.depositsInTransit)}</Money></dd></div>
                <div className="flex justify-between"><dt className="text-slate-500">Less: outstanding checks</dt><dd><Money>-{amount(recon.outstandingChecks)}</Money></dd></div>
                <div className="flex justify-between border-t border-slate-200 pt-1 font-semibold dark:border-slate-700"><dt>Adjusted bank balance</dt><dd><Money>{amount(recon.adjustedBank)}</Money></dd></div>
              </dl>
              <dl className="space-y-1 text-sm">
                <div className="flex justify-between"><dt className="text-slate-500">Balance per books</dt><dd><Money>{amount(recon.bookBalance)}</Money></dd></div>
                <div className="flex justify-between"><dt className="text-slate-500">Bank items not yet booked</dt><dd><Money>{amount(recon.unrecordedItems)}</Money></dd></div>
                <div className="flex justify-between border-t border-slate-200 pt-1 font-semibold dark:border-slate-700"><dt>Adjusted book balance</dt><dd><Money>{amount(recon.adjustedBook)}</Money></dd></div>
                <div className="flex justify-between font-bold"><dt>Difference</dt><dd><StatusChip tone={dec(recon.difference).isZero() ? 'ok' : 'critical'}>{amount(recon.difference)}</StatusChip></dd></div>
              </dl>
              <div className="md:col-span-2 flex justify-end">
                <Button disabled={!recon.canComplete} busy={complete.isPending} onClick={() => complete.mutate()}><Check aria-hidden="true" className="h-4 w-4" /> Complete and lock</Button>
              </div>
              {!recon.canComplete && <div className="md:col-span-2"><Alert tone="warning">To complete, the difference must be zero and every bank item must be booked or ignored.</Alert></div>}
            </div>
          )}
        </Card>
      )}
      {creating && <CreateFromLine line={creating} onClose={() => setCreating(null)} onDone={() => { setCreating(null); refresh(); }} />}
    </>
  );
}

function CreateFromLine({ line, onClose, onDone }: { line: StatementLine; onClose: () => void; onDone: () => void }): JSX.Element {
  const accounts = useAccounts();
  const incoming = dec(line.amount).isPositive();
  const [accountId, setAccountId] = useState('');
  const [taxCode, setTaxCode] = useState(incoming ? 'NONVAT' : 'VAT12');
  const [description, setDescription] = useState(line.description);
  const [finalTax, setFinalTax] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () => api(`/statement-lines/${line.id}/create-transaction`, { method: 'POST', body: { accountId, taxCode, description, finalTaxRate: incoming && finalTax ? '0.20' : null } }),
    onSuccess: onDone,
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Could not book it.'),
  });
  return (
    <Modal title={incoming ? 'Money in' : 'Money out'} description={`${shortDate(line.date)} · ${line.description} · ${amount(line.amount)}`} onClose={onClose}>
      <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
        {error && <Alert tone="critical">{error}</Alert>}
        <Field label="Account" required><AccountSelect accounts={accounts.data ?? []} value={accountId} onChange={setAccountId} /></Field>
        {!incoming && (
          <Field label="Tax" hint="Bank service charges usually carry VAT.">
            <select className={inputClass} value={taxCode} onChange={(e) => setTaxCode(e.target.value)}>
              <option value="VAT12">VAT 12% (input VAT claimable)</option>
              <option value="NONVAT">No VAT</option>
            </select>
          </Field>
        )}
        {incoming && <label className="flex items-center gap-2 text-sm"><input type="checkbox" className="h-4 w-4" checked={finalTax} onChange={(e) => setFinalTax(e.target.checked)} /> Interest, net of 20% final tax withheld by the bank</label>}
        <Field label="Description"><input className={inputClass} value={description} onChange={(e) => setDescription(e.target.value)} /></Field>
        <div className="flex justify-end gap-2"><Button type="button" variant="secondary" onClick={onClose}>Cancel</Button><Button type="submit" busy={save.isPending} disabled={!accountId}>Book and match</Button></div>
      </form>
    </Modal>
  );
}
